import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { Link } from "react-router-dom";
import { ScRequestMaterial } from "./model";

/**
 * Minimal cell renderer that links its cell value to the material details page,
 * matching the actual material log (same `gridIdLink` styling and URL). Shared
 * by the ID and Name columns instead of the log's `MaterialSequenceIdLinkCellRenderer`
 * / `twoRowCellRenderer`, which also bundle edit icons, description rows and
 * AI-insights logic we don't want on this read-only page.
 */
function MaterialDetailsLinkRenderer({
  data,
  value,
  projectId
}: ICellRendererParams<ScRequestMaterial> & { projectId: string }) {
  if (!data) return null;
  return (
    <Link
      className="gridIdLink"
      to={`/project/${projectId}/materials/${data.id}`}
    >
      {value}
    </Link>
  );
}

/** Full name of a material's assignee, or "" when unassigned. */
function getAssigneeName(data?: ScRequestMaterial): string {
  const assignee = data?.assignee_user;
  if (!assignee?.id) return "";
  return [assignee.first_name, assignee.last_name].filter(Boolean).join(" ");
}

/**
 * Assignee cell: the person's name with their company on a second line (in
 * gray), matching the actual material log's `userEntityCellRenderer`. Kept as a
 * minimal local renderer — no status icons or edit dropdown.
 */
function AssigneeCellRenderer({
  data
}: ICellRendererParams<ScRequestMaterial>) {
  const name = getAssigneeName(data);
  if (!name) return null;
  const companyName = data?.assignee_user?.company?.name;
  return (
    <div className="flex flex-col leading-none space-y-1.5 overflow-hidden">
      <div className="truncate">{name}</div>
      {companyName && (
        <div className="text-ciq-gray-800 truncate">{companyName}</div>
      )}
    </div>
  );
}

/**
 * Plain, read-only column definitions for the SC-request material log.
 *
 * Deliberately uses only native AG Grid text rendering (raw `field` /
 * `valueGetter` strings) — no shared cell renderers or col-def factories, which
 * carry editing/business logic we don't want here. Status shows the raw
 * `milestone_state` string as-is.
 *
 * The exceptions are the ID and Name columns, which — like the actual material
 * log — link to the material details page via the minimal local renderer above.
 */
export const getScRequestColumnDefs = (
  materialHeaderMap: any,
  projectId: string
): ColDef<ScRequestMaterial>[] => [
  {
    colId: "material_id",
    field: "sequence_id",
    headerName: materialHeaderMap?.material_id || "Material ID",
    initialWidth: 90,
    // Matches the actual material log, which disables the filter on this column.
    filter: false,
    cellRenderer: MaterialDetailsLinkRenderer,
    cellRendererParams: { projectId }
  },
  {
    colId: "name",
    field: "title",
    headerName: materialHeaderMap?.name || "Name",
    tooltipField: "title",
    cellRenderer: MaterialDetailsLinkRenderer,
    cellRendererParams: { projectId }
  },
  {
    colId: "status",
    field: "milestone_state",
    headerName: materialHeaderMap?.status || "Status"
  },
  {
    colId: "trade_partner",
    headerName: materialHeaderMap?.trade_partner || "Responsible Contractor",
    valueGetter: ({ data }) => data?.responsible_contractor?.name ?? ""
  },
  {
    colId: "assignee",
    headerName: materialHeaderMap?.assignee || "Assignee",
    cellRenderer: AssigneeCellRenderer,
    // Include the company in the filter/sort/search value, like the real log.
    valueGetter: ({ data }) => {
      const name = getAssigneeName(data);
      if (!name) return "";
      const companyName = data?.assignee_user?.company?.name;
      return companyName ? `${name} ( ${companyName} )` : name;
    }
  },
  {
    // Empty trailing "spacer" column. The data columns keep their natural
    // widths; this flexes to absorb the remaining grid width so the row
    // separator lines extend edge-to-edge instead of stopping at the last
    // data column. Purely visual and inert — no header, sort, filter, menu,
    // move or keyboard navigation. minWidth 0 lets it collapse to nothing when
    // the data columns already fill the grid (so it never forces a scrollbar).
    colId: "spacer",
    headerName: "",
    flex: 1,
    minWidth: 0,
    sortable: false,
    filter: false,
    resizable: false,
    suppressMovable: true,
    suppressNavigable: true,
    lockPosition: "right"
  }
];

export const scRequestDefaultColDef: ColDef<ScRequestMaterial> = {
  sortable: true,
  resizable: true,
  editable: false,
  // Same as the actual material log: the default (text) filter, exposed via the
  // filter menu tab only. Individual columns can still opt out with filter:false.
  filter: true,
  minWidth: 100
};
